import React, { useEffect, useMemo, useState } from "react";
import { LockClosedIcon } from "@heroicons/react/outline";
import Cookies from "js-cookie";
import { format } from "date-fns";
import { v4 as uuidv4 } from "uuid";
import { Constants } from "config/constants";
import StatusBadge from "./StatusBadge";

const dateFormat = "yyyy-MM-dd";

const formatDateValue = (value) => value || "";

const restrictedLifecycleValues = new Set(["archived", "delete"]);

const ComplianceTaskRow = ({
  corporateId,
  categoryId,
  task,
  statusOptions,
  lifecycleOptions,
  frequencyOptions,
  timelineTypes,
  availableUsers,
  isCompanyView = false,
  canEditScheduling = false,
  canAssignResponsibility = false,
  checklistHeaderEditable = false,
  canChangeStatus = true,
  canEditLifecycle = false,
  onUpdateTask,
  onLifecycleChange,
  onAssignResponsibility,
  isOpen = false,
  onToggle = () => {},
}) => {
  const [showResponsibilityForm, setShowResponsibilityForm] = useState(false);
  const [responsibilityDraft, setResponsibilityDraft] = useState({
    individualId: "",
    title: "",
    department: "",
    initials: "",
  });

  useEffect(() => {
    if (!isOpen) {
      setShowResponsibilityForm(false);
    }
  }, [isOpen]);

  const currentRole = Cookies.get("role") || "";
  const isSuperAdminRole =
    currentRole === String(Constants.COOKIE_ROLES.SUPER_ADMIN);
  const isOrgAdminRole =
    currentRole === String(Constants.COOKIE_ROLES.ORG_ADMIN);
  const isMandatoryTask = Boolean(task.isMandatory);
  const isMandatoryLocked =
    isMandatoryTask &&
    ((task.mandatoryBy === "superAdmin" && !isSuperAdminRole) ||
      (task.mandatoryBy === "orgAdmin" && !isOrgAdminRole) ||
      (!task.mandatoryBy && !(isSuperAdminRole || isOrgAdminRole)));

  const currentStatusDate =
    task.status === "completed" ? task.completionDate : task.statusDate;

  const handleStatusChange = (event) => {
    if (!canChangeStatus) return;
    const { value } = event.target;
    const today = format(new Date(), dateFormat);
    const updates = { status: value };
    if (value === "completed") {
      updates.completionDate = task.completionDate || today;
      updates.statusDate = today;
    } else {
      updates.statusDate = task.statusDate || today;
      updates.completionDate = null;
    }
    onUpdateTask(categoryId, task.id, updates);
  };

  const handleDateChange = (field) => (event) => {
    if (
      (field === "statusDate" || field === "completionDate") &&
      !canChangeStatus
    ) {
      return;
    }
    const { value } = event.target;
    onUpdateTask(categoryId, task.id, { [field]: value });
  };

  const handleTimelineTypeChange = (event) => {
    const { value } = event.target;
    const updates = { timelineType: value };
    if (value === "definite") {
      updates.frequency = null;
      updates.nextCheckDate = null;
    } else {
      updates.frequency = task.frequency || frequencyOptions[0]?.value;
      updates.deadline = task.deadline || task.nextCheckDate || task.startDate;
    }
    onUpdateTask(categoryId, task.id, updates);
  };

  const handleFrequencyChange = (event) => {
    const { value } = event.target;
    onUpdateTask(categoryId, task.id, { frequency: value });
  };

  const handleLifecycleChange = (event) => {
    const { value } = event.target;
    if (!canEditLifecycle) {
      return;
    }
    if (isMandatoryLocked && restrictedLifecycleValues.has(value)) {
      return;
    }
    onLifecycleChange(categoryId, task.id, value);
  };

  const handleAddResponsibility = (event) => {
    event.preventDefault();
    if (!responsibilityDraft.individualId) return;
    const selectedUser = availableUsers.find(
      (user) => user.id === responsibilityDraft.individualId
    );
    const nextResponsibility = {
      id: uuidv4(),
      individualId: responsibilityDraft.individualId,
      title: responsibilityDraft.title || selectedUser?.title || "",
      department:
        responsibilityDraft.department || selectedUser?.department || "",
      name: selectedUser?.name,
      initials: responsibilityDraft.initials || selectedUser?.initials || "",
    };
    const updated = [
      ...(task.responsibilities || []).filter(
        (resp) => resp.individualId !== nextResponsibility.individualId
      ),
      nextResponsibility,
    ];
    onAssignResponsibility(categoryId, task.id, updated);
    setResponsibilityDraft({
      individualId: "",
      title: "",
      department: "",
      initials: "",
    });
    setShowResponsibilityForm(false);
  };

  const handleResponsibilityRemove = (responsibilityId) => {
    const updated = (task.responsibilities || []).filter(
      (resp) => resp.id !== responsibilityId
    );
    onAssignResponsibility(categoryId, task.id, updated);
  };

  const resolveInitials = (responsibility) => {
    if (responsibility.initials) return responsibility.initials;
    const user = availableUsers.find(
      (candidate) => candidate.id === responsibility.individualId
    );
    if (user?.initials) return user.initials;
    if (responsibility.name) {
      return responsibility.name
        .split(" ")
        .filter(Boolean)
        .map((part) => part.charAt(0))
        .join("")
        .slice(0, 4)
        .toUpperCase();
    }
    return "NA";
  };

  const taskWrapperClasses = useMemo(() => {
    if (task.lifecycleStatus !== "active") {
      return "border-gray-200 bg-gray-50 opacity-90";
    }
    if (task.summaryStatus === "notCompliant") {
      return "border-red-200 bg-red-50";
    }
    if (task.summaryStatus === "compliant") {
      return "border-emerald-200 bg-emerald-50";
    }
    if (task.summaryStatus === "notDue") {
      return "border-emerald-100 bg-emerald-50";
    }
    return "border-amber-100 bg-amber-50";
  }, [task.lifecycleStatus, task.summaryStatus]);

  const statusLabel = useMemo(() => {
    const option = statusOptions.find((item) => item.value === task.status);
    return option?.label || task.status;
  }, [statusOptions, task.status]);

  const lifecycleLabel = useMemo(() => {
    const option = lifecycleOptions.find(
      (item) => item.value === task.lifecycleStatus
    );
    return option?.label || task.lifecycleStatus;
  }, [lifecycleOptions, task.lifecycleStatus]);

  const timelineLabel =
    String(task.timelineType).toLowerCase() === "definite"
      ? "Definite"
      : "Indefinite";

  const summaryDateValue =
    String(task.timelineType).toLowerCase() === "definite"
      ? task.deadline
      : task.nextCheckDate || task.deadline;

  const summaryDateLabel = summaryDateValue
    ? formatDateValue(summaryDateValue)
    : null;

  const weightLabel = Number(task.weight || 0).toFixed(2);

  return (
    <div
      className={`rounded-xl border transition-colors ${taskWrapperClasses}`}
    >
      <div className="p-4">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={Boolean(isOpen)}
          className="flex w-full items-start justify-between gap-4 text-left focus:outline-none"
        >
          <div className="flex flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="text-base font-semibold text-gray-800">
                  {task.title}
                </h4>
                {isMandatoryTask && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-sflPrimary/40 bg-sflPrimary/10 px-2.5 py-0.5 text-[0.6875rem] font-semibold text-sflPrimary">
                    <LockClosedIcon
                      className="h-3.5 w-3.5"
                      aria-hidden="true"
                    />
                    <span>Mandatory</span>
                  </span>
                )}
              </div>
              <StatusBadge
                label={task.complianceTag}
                colorOverride={task.complianceColor}
              />
              {task.lifecycleStatus !== "active" && (
                <StatusBadge
                  label={
                    task.lifecycleStatus === "archived" ? "Archived" : "N/A"
                  }
                />
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              <span>
                Status:{" "}
                <span className="font-semibold text-gray-700">
                  {statusLabel}
                </span>
              </span>
              <span>
                Lifecycle:{" "}
                <span className="font-semibold text-gray-700">
                  {lifecycleLabel}
                </span>
              </span>
              <span>
                Timelinse:{" "}
                <span className="font-semibold text-gray-700">
                  {timelineLabel}
                </span>
              </span>
              {summaryDateLabel && (
                <span>
                  Next Check:{" "}
                  <span className="font-semibold text-gray-700">
                    {summaryDateLabel}
                  </span>
                </span>
              )}
              <span>
                Weight:{" "}
                <span className="font-semibold text-gray-700">
                  {weightLabel}%
                </span>
              </span>
            </div>
          </div>
          <span
            className={`mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-sm font-semibold text-gray-500 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          >
            ▾
          </span>
        </button>

        {isOpen && (
          <div className="mt-4 space-y-4 border-t border-white/60 pt-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="flex-1 space-y-2">
                {task.description && (
                  <p className="text-sm text-gray-600">{task.description}</p>
                )}
                {task.dependency && (
                  <p className="text-xs text-gray-500">
                    Dependency:{" "}
                    <span className="font-medium">{task.dependency}</span>
                  </p>
                )}
                {task.notes && (
                  <p className="text-xs text-gray-500">Note: {task.notes}</p>
                )}
                <div className="mt-2 max-w-sm">
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Checklist Header
                  </label>
                  <input
                    type="text"
                    value={task.checklistHeader || ""}
                    onChange={(event) => {
                      if (!checklistHeaderEditable) return;
                      onUpdateTask(categoryId, task.id, {
                        checklistHeader: event.target.value,
                      });
                    }}
                    disabled={!checklistHeaderEditable}
                    className={`mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none ${
                      !checklistHeaderEditable
                        ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                        : ""
                    }`}
                    placeholder="e.g. Statutory Registers"
                  />
                </div>
                <p className="mt-2 text-xs uppercase tracking-wide text-gray-500">
                  Weight Contribution: {weightLabel}%
                </p>
              </div>
              <div className="flex flex-col gap-3 md:w-64">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Status
                </label>
                <select
                  value={task.status}
                  onChange={handleStatusChange}
                  disabled={!canChangeStatus}
                  className={`rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none ${
                    !canChangeStatus
                      ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {task.status === "completed"
                    ? "Completion Date"
                    : "Status Date"}
                </label>
                <input
                  type="date"
                  value={formatDateValue(currentStatusDate)}
                  onChange={handleDateChange(
                    task.status === "completed"
                      ? "completionDate"
                      : "statusDate"
                  )}
                  disabled={!canChangeStatus}
                  className={`rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none ${
                    !canChangeStatus
                      ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                      : ""
                  }`}
                />
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Lifecycle
                </label>
                <select
                  value={task.lifecycleStatus}
                  onChange={handleLifecycleChange}
                  disabled={!canEditLifecycle}
                  className={`rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none ${
                    !canEditLifecycle
                      ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                      : ""
                  }`}
                >
                  {lifecycleOptions.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      disabled={
                        isMandatoryLocked &&
                        restrictedLifecycleValues.has(option.value)
                      }
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Timeline Type
                </label>
                <select
                  value={String(task.timelineType).toLowerCase()}
                  onChange={() => {}}
                  disabled={true}
                  className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none bg-gray-100 text-gray-500 cursor-not-allowed"
                >
                  <option value="definite">Definite</option>
                  <option value="indefinite">Indefinite</option>
                </select>
              </div>
              {true && (
                <>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={formatDateValue(task.startDate)}
                      onChange={handleDateChange("startDate")}
                      disabled={!canEditScheduling}
                      className={`mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none ${
                        !canEditScheduling
                          ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                          : ""
                      }`}
                    />
                  </div>
                  {String(task.timelineType).toLowerCase() === "definite" ? (
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Deadline
                      </label>
                      <input
                        type="date"
                        value={formatDateValue(task.deadline)}
                        onChange={handleDateChange("deadline")}
                        disabled={!canEditScheduling}
                        className={`mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none ${
                          !canEditScheduling
                            ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                            : ""
                        }`}
                      />
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Frequency
                        </label>
                        <select
                          value={task.frequency || ""}
                          onChange={handleFrequencyChange}
                          disabled={!canEditScheduling}
                          className={`mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none ${
                            !canEditScheduling
                              ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                              : ""
                          }`}
                        >
                          {frequencyOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Next Check Date
                        </label>
                        <input
                          type="date"
                          value={formatDateValue(
                            task.nextCheckDate || task.deadline
                          )}
                          onChange={handleDateChange("nextCheckDate")}
                          disabled={!canEditScheduling}
                          className={`mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none ${
                            !canEditScheduling
                              ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                              : ""
                          }`}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Responsibility
                </label>
                <div className="mt-1 space-y-2">
                  {(task.responsibilities || []).map((responsibility) => (
                    <div
                      key={responsibility.id}
                      className="flex items-center justify-between rounded-md bg-white px-3 py-2 shadow-sm"
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sflPrimary/10 text-xs font-semibold uppercase text-sflPrimary">
                          {resolveInitials(responsibility)}
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-gray-700">
                            {responsibility.name ||
                              availableUsers.find(
                                (user) =>
                                  user.id === responsibility.individualId
                              )?.name ||
                              "Assigned User"}
                          </p>
                          <p className="text-xs text-gray-500">
                            {responsibility.title} • {responsibility.department}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          handleResponsibilityRemove(responsibility.id)
                        }
                        disabled={!canAssignResponsibility}
                        className={`text-xs font-semibold ${
                          canAssignResponsibility
                            ? "text-red-500 hover:text-red-600"
                            : "text-gray-400 cursor-not-allowed"
                        }`}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      canAssignResponsibility &&
                      setShowResponsibilityForm((value) => !value)
                    }
                    disabled={!canAssignResponsibility}
                    className={`text-xs font-semibold ${
                      canAssignResponsibility
                        ? "text-sflPrimary hover:underline"
                        : "text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    {showResponsibilityForm ? "Cancel" : "+ Add Responsibility"}
                  </button>
                  {showResponsibilityForm && canAssignResponsibility && (
                    <form
                      className="space-y-2"
                      onSubmit={handleAddResponsibility}
                    >
                      <select
                        value={responsibilityDraft.individualId}
                        onChange={(event) => {
                          const { value } = event.target;
                          const user = availableUsers.find(
                            (candidate) => candidate.id === value
                          );
                          setResponsibilityDraft((prev) => ({
                            ...prev,
                            individualId: value,
                            title: user?.title || "",
                            department: user?.department || "",
                            initials: (user?.initials || "").toUpperCase(),
                          }));
                        }}
                        className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                        required
                      >
                        <option value="">Select user</option>
                        {availableUsers.map((user) => (
                          <option key={user.id} value={user.id}>
                            {`${user.name}`
                              .trim()
                              .replace(
                                /\w\S*/g,
                                (word) =>
                                  word.charAt(0).toUpperCase() +
                                  word.slice(1).toLowerCase()
                              )}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Title"
                        value={responsibilityDraft.title}
                        onChange={(event) =>
                          setResponsibilityDraft((prev) => ({
                            ...prev,
                            title: event.target.value,
                          }))
                        }
                        className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                        required
                      />
                      <input
                        type="text"
                        placeholder="Department"
                        value={responsibilityDraft.department}
                        onChange={(event) =>
                          setResponsibilityDraft((prev) => ({
                            ...prev,
                            department: event.target.value,
                          }))
                        }
                        className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                        required
                      />
                      <input
                        type="text"
                        placeholder="Initials"
                        value={responsibilityDraft.initials}
                        onChange={(event) =>
                          setResponsibilityDraft((prev) => ({
                            ...prev,
                            initials: event.target.value
                              .slice(0, 4)
                              .toUpperCase(),
                          }))
                        }
                        className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm uppercase focus:border-sflPrimary focus:outline-none"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setResponsibilityDraft({
                              individualId: "",
                              title: "",
                              department: "",
                              initials: "",
                            });
                            setShowResponsibilityForm(false);
                          }}
                          className="rounded-md border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="rounded-md bg-sflPrimary px-3 py-1 text-xs font-semibold text-white"
                        >
                          Save
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceTaskRow;
